import {
  ApolloClient,
  ApolloLink,
  ApolloQueryResult,
  createHttpLink,
  DocumentNode,
  FetchResult,
  FieldPolicy,
  gql,
  HttpLink,
  InMemoryCache, NormalizedCacheObject,
  Observable,
  ServerError,
  split,
  TypePolicies,
} from '@apollo/client'
import { ErrorResponse, onError } from '@apollo/client/link/error'
import { createPersistedQueryLink } from '@apollo/client/link/persisted-queries'
import { RetryLink } from '@apollo/client/link/retry'
import { GraphQLWsLink } from '@apollo/client/link/subscriptions'

import { getMainDefinition } from 'apollo-utilities'
import { sha256 } from 'crypto-hash'
import { GraphQLFormattedError } from 'graphql'
import { createClient } from 'graphql-ws'
import { ParseError } from 'libphonenumber-js'
import { Observable as ErrorObservable } from 'zen-observable-ts'

import { Utilities } from './Utilities'

export interface FCMConfig {
  apiKey: string
  projectId: string
  senderId: string
  appId: string
  measurementId: string
  vapidKey: string
}
export interface InterfaceTypes {
  kind: string
  name: string
  possibleTypes: {
    name: string
  }[]
}

declare const window: Window
export abstract class ConfigPlugin {

  abstract typePolicies?(): TypePolicies
  abstract fieldPolicies?(): { [k: string]: FieldPolicy }
  abstract types?(): DocumentNode
  abstract fragments?(): DocumentNode
  abstract queries?(): DocumentNode
  abstract extensions?(): DocumentNode
  abstract headers?(): { [k: string]: string }
  abstract configure?(config: Config): Promise<void>
  abstract handleError?(error: GraphQLFormattedError | null, networError: ServerError | ParseError | null, handler: ErrorResponse, config: Config): ErrorObservable<FetchResult> | void
  abstract possibleTypes?(): { [k: string]: string[] }

}

// const starFleet = theFinalFrontier.find(/NCC1701*?/gi)
export class Config {

  token: string | null = null
  serverClient: ApolloClient<NormalizedCacheObject> | null = null
  browserClient: ApolloClient<NormalizedCacheObject> | null = null
  possibleTypes?: { [k: string]: string[] }
  plugins: ConfigPlugin[] = []

  registerPlugin(plugin: ConfigPlugin): void {
    this.plugins.push(plugin)
  }

  async wait(amount: number): Promise<void> {
    return new Promise((resolve) => {
      setTimeout(() => {
        resolve()
      }, amount)
    })
  }

  ssr(): boolean {
    if (this.isBrowser()) {
      return window.isSSRResponse
    }
    return false
  }

  ssrUserLoad(loading: boolean): boolean {
    if (this.isBrowser()) {
      return loading
    }
    return false
  }

  defaultImage(): string {
    return '/images/logo/logo_round.svg'
  }

  isBrowser(): boolean {
    return (typeof window !== 'undefined')
  }

  isProductionDomain(): boolean {
    return this.isBrowser() && window.location.hostname === 'www.ucook.co.za'
  }

  fetchSSRQuery(): boolean {
    return true
  }

  getPluginHeaders(): { [k: string]: string } {
    let headers = {
      Accept: 'multipart/mixed; deferSpec=20220824, application/json',
    }
    for (let p = 0; p < this.plugins.length; p++) {
      if (this.plugins[p].headers) {
        headers = {
          ...headers,
          ...this.plugins[p].headers(),
        }
      }
    }
    return headers
  }

  reset(): void {
    this.serverClient = null
    this.browserClient = null
  }

  typePolicies(): TypePolicies {
    let typePolicies: TypePolicies = {}
    for (let p = 0; p < this.plugins.length; p++) {
      if (this.plugins[p].typePolicies) {
        typePolicies = {
          ...typePolicies,
          ...this.plugins[p].typePolicies(),
        }
      }
    }
    return {
      ...typePolicies,
      Query: {
        fields: {
          ...this.fieldPolicies(),
        },
      },
    }
  }

  fieldPolicies(): { [k: string]: FieldPolicy } {
    let fieldPolicies: { [k: string]: FieldPolicy } = {}
    for (let p = 0; p < this.plugins.length; p++) {
      if (this.plugins[p].fieldPolicies) {
        fieldPolicies = {
          ...fieldPolicies,
          ...this.plugins[p].fieldPolicies(),
        }
      }
    }
    return {
      ...fieldPolicies,
    }
  }

  async getInterfaceTypes(): Promise<{ [k: string]: string[] }> {
    if (this.possibleTypes) {
      return this.possibleTypes
    }
    if (this.isBrowser() && window.possibleTypes) {
      this.possibleTypes = window.possibleTypes
      return this.possibleTypes
    }
    const qry = gql`
        query GetInterfaceConfig {
          types: getInterfaceTypes {
            kind
            name
            possibleTypes {
              name
            }
          }
        }
      `
    const possibleTypes: { [k: string]: string[] } = {}
    try {
      const client = this.getBasicClient()
      const result: ApolloQueryResult<{ types: InterfaceTypes[] }> = await client.query({
        query: qry,
      })
      result.data.types.forEach((type) => {
        possibleTypes[type.name] = type.possibleTypes.map((possibleType) => possibleType.name)
      })
      let pluginPossibleTypes: { [k: string]: string[] } = {}
      this.plugins.forEach((plugin) => {
        const pluginTypes = plugin.possibleTypes?.()
        if (pluginTypes) {
          pluginPossibleTypes = {
            ...pluginPossibleTypes,
            ...pluginTypes,
          }
        }
      })
      this.possibleTypes = { ...possibleTypes, ...pluginPossibleTypes }
    } catch (e) {
      console.log(e)
    }
    return possibleTypes
  }

  async getClient(): Promise<ApolloClient<NormalizedCacheObject>> {
    if (!this.possibleTypes) {
      if (this.isBrowser() && window.possibleTypes) {
        let pluginPossibleTypes: { [k: string]: string[] } = {}
        this.plugins.forEach((plugin) => {
          const pluginTypes = plugin.possibleTypes?.()
          if (pluginTypes) {
            pluginPossibleTypes = {
              ...pluginPossibleTypes,
              ...pluginTypes,
            }
          }
        })
        this.possibleTypes = { ...window.possibleTypes, ...pluginPossibleTypes }
      } else {
        await this.getInterfaceTypes()
      }
    }
    if (this.isBrowser()) {
      return this.getBrowserClient(this.possibleTypes)
    }
    return this.getServerClient(this.possibleTypes)
  }

  getBrowserCache(possibleTypes: { [k: string]: string[] }): InMemoryCache {
    return new InMemoryCache({
      possibleTypes,
      typePolicies: this.typePolicies(),
    })
  }

  async getBrowserLinks(): Promise<ApolloLink> {
    const errorLink = onError((handler: ErrorResponse) => {
      const { graphQLErrors, networkError } = handler
      const plugins = this.plugins.filter((plugin) => plugin.handleError)

      if (graphQLErrors) {
        for (const err of graphQLErrors) {
          for (let p = 0; p < plugins.length; p++) {
            const response = plugins[p].handleError?.(err, null, handler, this)
            if (response instanceof ErrorObservable) {
              return response
            }
          }
        }

        graphQLErrors.forEach(({ message, locations, path }) =>
          console.error(`[GraphQL error]: Message: ${message}, Location: ${locations}, Path: ${path}`),
        )
      }

      if (networkError) {
        console.error(`[Network error]: ${networkError}`)
        for (let p = 0; p < plugins.length; p++) {
          const response = plugins[p].handleError?.(null, networkError, handler, this)

          if (response instanceof Observable) {
            return response
          }
        }
      }
    })

    const middlewareLink = new ApolloLink((operation, forward) => {
      operation.setContext({
        headers: {
          ...operation.getContext().headers,
          ...this.getPluginHeaders(),
        },
      })
      return forward(operation)
    })

    const retryLink = new RetryLink({
      attempts: {
        max: 10,
        retryIf: (error) => error.message === 'Failed to fetch',
      },
      delay: {
        initial: 500,
        max: 30000,
        jitter: true,
      },
    })

    const httpLink = new HttpLink({
      fetch,
      uri: this.getApi(),
    })

    const wsLink = new GraphQLWsLink(
      createClient({
        url: this.getApi(true),
        connectionParams: {
          ...this.getPluginHeaders(),
        },
      }),
    )

    const transportLink = split(
      ({ query }) => {
        const definition = getMainDefinition(query)
        return definition.kind === 'OperationDefinition' && definition.operation === 'subscription'
      },
      wsLink,
      httpLink,
    )

    if (process.env.NODE_ENV === 'production') {
      const persistentLink = createPersistedQueryLink({ sha256 })
      return middlewareLink
        .concat(persistentLink)
        .concat(retryLink)
        .concat(errorLink)
        .concat(transportLink)
    }

    return middlewareLink.concat(retryLink).concat(errorLink).concat(transportLink)
  }

  async getBrowserClient(possibleTypes: { [k: string]: string[] }): Promise<ApolloClient<NormalizedCacheObject>> {
    if (!this.browserClient) {

      const cache = new InMemoryCache({
        possibleTypes,
        typePolicies: this.typePolicies(),
      })

      if (this.ssr()) {
        cache.restore(window.__APOLLO_STATE__)
      }

      const link = await this.getBrowserLinks()

      this.browserClient = new ApolloClient({
        link,
        cache,
        connectToDevTools: true,
        ssrMode: this.ssr(),
      })
    }

    return this.browserClient
  }

  async configureApp(): Promise<void> {
    await this.getInterfaceTypes()
    for (let p = 0; p < this.plugins.length; p++) {
      await this.plugins[p].configure?.(this)
    }
  }

  getServerClient(possibleTypes: { [k: string]: string[] }): ApolloClient<NormalizedCacheObject> {

    const httpLink = createHttpLink({
      // eslint-disable-next-line
      // @ts-ignore
      fetch,
      uri: this.getApi(),
    })

    const middlewareLink = new ApolloLink((operation, forward): Observable<FetchResult> => {
      operation.setContext({
        headers: {
          ...operation.getContext().headers,
          ...this.getPluginHeaders(),
        },
      })
      return forward(operation)
    })

    const retryLink = new RetryLink({
      attempts: {
        max: 10,
        retryIf: (error): boolean => error.message === 'Failed to fetch',
      },
      delay: {
        initial: 500,
        max: 30000,
        jitter: true,
      },
    })

    const link = middlewareLink.concat(retryLink).concat(httpLink)

    return new ApolloClient({
      link,
      cache: new InMemoryCache({
        possibleTypes,
        typePolicies: this.typePolicies(),
      }),
      ssrMode: this.ssr(),
    })
  }

  getBasicClient(token?: string): ApolloClient<NormalizedCacheObject> {
    console.log(`USING: ${this.getApi()} FOR SERVER SIDE REQUEST`)
    const httpLink = createHttpLink({
      // eslint-disable-next-line
      // @ts-ignore
      fetch,
      uri: this.getApi(),
    })
    let headers = {
      ...this.getPluginHeaders(),
    }
    if (token) {
      headers = {
        Authorization: `Bearer ${token}`,
      }
    }
    const middlewareLink = new ApolloLink((operation, forward): Observable<FetchResult> => {
      operation.setContext({
        headers: {
          ...operation.getContext().headers,
          ...headers,
        },
      })
      return forward(operation)
    })

    const retryLink = new RetryLink({
      attempts: {
        max: 10,
        retryIf: (error): boolean => error.message === 'Failed to fetch',
      },
      delay: {
        initial: 500,
        max: 30000,
        jitter: true,
      },
    })

    const link = middlewareLink.concat(retryLink).concat(httpLink)

    return new ApolloClient({
      link,
      cache: new InMemoryCache({
        typePolicies: this.typePolicies(),
      }),
      ssrMode: true,
    })
  }

  getENV(): string {
    if (this.isBrowser()) {
      return window.env.ENVIRONMENT
    } else {
      return process.env.NODE_ENV
    }
  }

  getApi(socket = false): string {
    if (this.isBrowser()) {
      if (!window.env.API_PORT) {
        return `${socket ? window.env.API_PROTOCOL === 'https' ? 'wss' : 'ws' : window.env.API_PROTOCOL}://${window.env.API_DOMAIN}/${window.env.API_ENDPOINT}`
      }
      return `${socket ? window.env.API_PROTOCOL === 'https' ? 'wss' : 'ws' : window.env.API_PROTOCOL}://${window.env.API_DOMAIN}:${window.env.API_PORT}/${window.env.API_ENDPOINT}`
    } else {
      if (process.env.API_SERVER) {
        const servers = process.env.API_SERVER.split('|')
        return servers[Utilities.randomInt(0, servers.length - 1)]
      }
      if (!process.env.API_PORT) {
        return `${socket ? process.env.API_PROTOCOL === 'https' ? 'wss' : 'ws' : process.env.API_PROTOCOL}://${process.env.API_DOMAIN}/${process.env.API_ENDPOINT}`
      }
      return `${socket ? process.env.API_PROTOCOL === 'https' ? 'wss' : 'ws' : process.env.API_PROTOCOL}://${process.env.API_DOMAIN}:${process.env.API_PORT}/${process.env.API_ENDPOINT}`
    }
  }

  getApiBaseUrl(socket = false): string {
    if (this.isBrowser()) {
      if (!window.env.API_PORT) {
        return `${socket ? window.env.API_PROTOCOL === 'https' ? 'wss' : 'ws' : window.env.API_PROTOCOL}://${window.env.API_DOMAIN}`
      }
      return `${socket ? window.env.API_PROTOCOL === 'https' ? 'wss' : 'ws' : window.env.API_PROTOCOL}://${window.env.API_DOMAIN}:${window.env.API_PORT}`
    } else {
      if (process.env.API_SERVER) {
        const servers = process.env.API_SERVER.split('|')
        const server = servers[Utilities.randomInt(0, servers.length - 1)]
        return server.replace(`/${process.env.API_ENDPOINT}`, '')
      }
      if (!process.env.API_PORT) {
        return `${socket ? process.env.API_PROTOCOL === 'https' ? 'wss' : 'ws' : process.env.API_PROTOCOL}://${process.env.API_DOMAIN}`
      }
      return `${socket ? process.env.API_PROTOCOL === 'https' ? 'wss' : 'ws' : process.env.API_PROTOCOL}://${process.env.API_DOMAIN}:${process.env.API_PORT}`
    }
  }

  getBaseUrl(excludePort = false): string {
    if (this.isBrowser()) {
      if (!window.env.PORT || excludePort) {
        return `${window.env.PROTOCOL}://${window.env.DOMAIN}`
      }
      return `${window.env.PROTOCOL}://${window.env.DOMAIN}:${window.env.PORT}`
    } else {
      if (!process.env.API_PORT || excludePort) {
        return `${process.env.PROTOCOL}://${process.env.DOMAIN}`
      }
      return `${process.env.PROTOCOL}://${process.env.DOMAIN}:${process.env.PORT}`
    }
  }

  getFacebook(): { appId: string, clientToken: string } {
    if (this.isBrowser()) {
      return {
        appId: window.env.FACEBOOK_APP_ID,
        clientToken: window.env.FACEBOOK_CLIENT_TOKEN,
      }
    }
    return {
      appId: process.env.FACEBOOK_APP_ID,
      clientToken: window.env.FACEBOOK_CLIENT_TOKEN,
    }
  }

  getGoogle(): { clientId: string, apiKey: string, iosClientId: string } {
    if (this.isBrowser()) {
      return {
        clientId: window.env.GOOGLE_CLIENT_ID,
        apiKey: window.env.GOOGLE_API_KEY,
        iosClientId: window.env.GOOGLE_IOS_CLIENT_ID,
      }
    }
    return {
      clientId: process.env.GOOGLE_CLIENT_ID,
      apiKey: process.env.GOOGLE_API_KEY,
      iosClientId: process.env.GOOGLE_IOS_CLIENT_ID,
    }
  }

  getGM(): string {
    if (this.isBrowser()) {
      return window.env.GOOGLE_API_KEY
    }
    return process.env.GOOGLE_API_KEY
  }

  getGA(): string {
    if (this.isBrowser()) {
      return window.env.GA_KEY
    }
    return process.env.GA_KEY
  }

  getFBPixel(): string {
    if (this.isBrowser()) {
      return window.env.FB_PIXEL_KEY
    }
    return process.env.FB_PIXEL_KEY
  }

  getOneSignal(): string {
    if (this.isBrowser()) {
      return window.env.ONE_SIGNAL_APP_ID
    }
    return process.env.ONE_SIGNAL_APP_ID
  }

  getFCM(): FCMConfig {
    if (this.isBrowser()) {
      return {
        apiKey: window.env.FCM_API_KEY,
        projectId: window.env.FCM_PROJECT_ID,
        senderId: window.env.FCM_SENDER_ID,
        appId: window.env.FCM_APP_ID,
        measurementId: window.env.FCM_MEASUREMENT_ID,
        vapidKey: window.env.FCM_VAPID_KEY,
      }
    }
    return {
      apiKey: process.env.FCM_API_KEY,
      projectId: process.env.FCM_PROJECT_ID,
      senderId: process.env.FCM_SENDER_ID,
      appId: process.env.FCM_APP_ID,
      measurementId: process.env.FCM_MEASUREMENT_ID,
      vapidKey: process.env.FCM_VAPID_KEY,
    }
  }

}
